/* eslint-disable react-refresh/only-export-components */
import {
  Outlet,
  createFileRoute,
  notFound,
  stripSearchParams,
} from '@tanstack/react-router'
import * as v from 'valibot'

import {
  CompanyPage,
  CompanyPageLoading,
} from '@/app/manager/CompanyPage/CompanyPage'
import { PageContainer } from '@/app/manager/Layout'
import { NoLicensesToolbar } from '@/app/manager/NoLicensesToolbar/NoLicensesToolbar'
import { BatchOperations } from '@/batch-operations/components/BatchOperations/BatchOperations'
import {
  FEATURE_BATCH_OPERATIONS,
  FEATURE_ENHANCED_DATA_TABLE,
} from '@/core/constants/features'
import { TableSelectionProvider } from '@/core/contexts/TableSelection'
import { buildPageTitle } from '@/core/utils/buildPageTitle/buildPageTitle'
import useFeatures from '@/core/utils/features/useFeatures'
import { batchOperationsQueries } from '@/http/api/batchOperations'

export const defaultQueryValues = {
  page: 0,
  size: 10,
  sort_by: 'uploaded_at' as const,
  sort_order: 'desc' as const,
  filename: '',
  operation: undefined,
  creator_id: undefined,
  creator_name: undefined,
  status: [],
  from_uploaded_at: undefined,
  to_uploaded_at: undefined,
  hidden_columns: [],
}

export const statusSchema = v.array(
  v.union([
    v.literal('pending'),
    v.literal('failed'),
    v.literal('parsing'),
    v.literal('parsed'),
    v.literal('processing'),
    v.literal('completed'),
  ]),
)

const baseQueryParamsSchema = v.object({
  page: v.optional(v.number(), defaultQueryValues.page),
  size: v.optional(v.number(), defaultQueryValues.size),
  sort_by: v.optional(
    v.union([
      v.literal('filename'),
      v.literal('status'),
      v.literal('uploaded_at'),
    ]),
    defaultQueryValues.sort_by,
  ),
  sort_order: v.optional(
    v.union([v.literal('asc'), v.literal('desc')]),
    defaultQueryValues.sort_order,
  ),
  filename: v.optional(v.string(), defaultQueryValues.filename),
  operation: v.optional(
    v.union([v.literal('user_import')]),
    defaultQueryValues.operation,
  ),
  creator_id: v.optional(v.string(), defaultQueryValues.creator_id),
  creator_name: v.optional(v.string(), defaultQueryValues.creator_name),
  status: v.optional(
    v.array(
      v.union([
        v.literal('pending'),
        v.literal('failed'),
        v.literal('parsing'),
        v.literal('parsed'),
        v.literal('processing'),
        v.literal('completed'),
      ]),
    ),
    defaultQueryValues.status,
  ),
  from_uploaded_at: v.optional(v.string(), defaultQueryValues.from_uploaded_at),
  to_uploaded_at: v.optional(v.string(), defaultQueryValues.to_uploaded_at),
})

const columnsSchema = v.object({
  // Columns the user has hidden. Absent from the URL means every column is
  // visible; "reset to default" clears this back to an empty list.
  hidden_columns: v.optional(
    v.array(v.picklist(['operation', 'uploaded_at', 'status', 'entries'])),
    defaultQueryValues.hidden_columns,
  ),
})

export type BatchOperationsHiddenColumn = v.InferOutput<
  typeof columnsSchema
>['hidden_columns'][number]

const searchQueryParamsSchema = v.object({
  ...baseQueryParamsSchema.entries,
  ...columnsSchema.entries,
})

export const Route = createFileRoute('/company/_management/batch-operations')({
  head: () => ({
    meta: [
      {
        title: buildPageTitle({
          titleKey: 'page-title-batch-operations',
        }),
      },
    ],
  }),
  beforeLoad: ({ context: { user } }) => {
    if (!user?.features.includes(FEATURE_BATCH_OPERATIONS)) {
      throw notFound()
    }
  },
  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(
      batchOperationsQueries.list({
        page: defaultQueryValues.page,
        size: defaultQueryValues.size,
        sort_by: defaultQueryValues.sort_by,
        sort_order: defaultQueryValues.sort_order,
        filename: defaultQueryValues.filename,
        operation: defaultQueryValues.operation,
        creator_id: defaultQueryValues.creator_id,
        status: defaultQueryValues.status,
        from_uploaded_at: defaultQueryValues.from_uploaded_at,
        to_uploaded_at: defaultQueryValues.to_uploaded_at,
      }),
    )
  },
  component: RouteComponent,
  pendingComponent: CompanyPageLoading,
  validateSearch: searchQueryParamsSchema,
  search: {
    middlewares: [stripSearchParams(defaultQueryValues)],
  },
})

function RouteComponent() {
  const hasEnhancedDataTableFeature = useFeatures(FEATURE_ENHANCED_DATA_TABLE)

  return (
    <TableSelectionProvider>
      <CompanyPage blockScroll={hasEnhancedDataTableFeature}>
        <NoLicensesToolbar />
        <PageContainer
          py="10"
          flex="1"
          minH="0"
          display="flex"
          flexDirection="column">
          <BatchOperations />
          <Outlet />
        </PageContainer>
      </CompanyPage>
    </TableSelectionProvider>
  )
}
